<template>
  <div class="time-mobile">
    <!-- Filter summary bar -->
    <time-filter-row 
      @click="filterVisible = true"
      :filter="filter"
      :count="rows.length"
      :total="total"
      :hours="hours"
      :money="money"
    ></time-filter-row>

    <!-- Action buttons -->
    <div class="container-fluid px-0 my-2">
      <div class="row g-2">
        <div class="col-4">
          <button @click="refresh()"
            class="btn btn-primary form-control"
          >
            <i class="fa fa-sync"></i> Refresh
          </button>
        </div>
        <div class="col-4">
          <button @click="newRecord()"
            class="btn btn-success form-control"
          >
            <i class="fa fa-plus"></i> New
          </button>
        </div>
        <div class="col-4">
          <button @click="filterVisible = true"
            class="btn btn-outline-secondary form-control"
          >
            <i class="fa fa-filter"></i> Filter
          </button>
        </div>
      </div>
    </div>

    <hr class="my-2" />

    <!-- Mobile-optimized list view -->
    <div class="mobile-time-list">
      <div v-for="row in rows" :key="row.Id" class="time-card mb-2" @click="editRecord(row.Id)">
        <div class="card">
          <div class="card-body p-2">
            <div class="d-flex justify-content-between align-items-start mb-2">
              <div>
                <strong>{{ row.Contact?.Code || 'N/A' }}</strong>
                <div class="text-muted small">{{ row.Contact?.Company || 'N/A' }}</div>
              </div>
              <div class="text-end">
                <span class="badge bg-primary">
                  {{ row.ActualTime || row.EstimatedTime || 0 }}h
                </span>
              </div>
            </div>
            
            <div class="small mb-1">
              <i class="fas fa-user me-1"></i>{{ row.Technician?.TechnicianName || 'N/A' }}
            </div>
            
            <div class="small mb-1" v-if="row.Request?.Id">
              <i class="fas fa-ticket-alt me-1"></i>Request #{{ row.Request.Id }}
            </div>
            
            <div class="small mb-1" v-if="row.WorkCompleteDate">
              <i class="fas fa-calendar me-1"></i>{{ getLocalDate(row.WorkCompleteDate) }}
            </div>
            
            <div class="small text-truncate" v-if="row.WorkAssignment">
              <strong>Assignment:</strong> {{ row.WorkAssignment }}
            </div>
            
            <div class="small text-truncate" v-if="row.ActualWork">
              <strong>Work Done:</strong> {{ row.ActualWork }}
            </div>
            
            <div class="mt-2" v-if="row.Status">
              <span :class="getStatusBadgeClass(row.Status)" class="badge">
                {{ getStatusName(row.Status) }}
              </span>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Empty state -->
      <div v-if="rows.length === 0" class="text-center text-muted py-5">
        <i class="fas fa-inbox fa-3x mb-3"></i>
        <p>No time entries found</p>
      </div>
    </div>

    <!-- Filter Modal -->
    <kt-modal 
      :visible="filterVisible" 
      title="Filter Time Entries" 
      @dismiss="filterVisible = false"
    >
      <template #body>
        <time-filter
          :filter="filter" 
          @dismiss="filterVisible = false" 
          @update="setFilter($event)"
        ></time-filter>
      </template>
    </kt-modal>
  </div>
</template>

<style scoped>
.time-mobile {
  padding: 0.5rem;
}

.time-card {
  cursor: pointer;
  transition: transform 0.2s;
}

.time-card:active {
  transform: scale(0.98);
}

.time-card .card {
  border-left: 3px solid #0d6efd;
}

.mobile-time-list {
  max-height: calc(100vh - 250px);
  overflow-y: auto;
}
</style>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from "vue";
import { useRouter } from "vue-router";
import Helper from "@/services/Helper";
import KtModal from "@/components/UI/KtModal.vue";
import TimeFilterRow from "@/components/Time/TimeFilterRow.vue";
import TimeFilter from "@/components/Time/TimeFilter.vue";
import { TimeModel } from "@/models/TaskModel";
import { useTaskStatusStore } from "@/stores/TaskStatusStore";
import { storeToRefs } from "pinia";

// Props - receive data from parent
const props = defineProps<{
  rows: TimeModel[];
  total: number;
  filter: any;
}>();

// Emits
const emit = defineEmits<{
  refresh: [];
  newRecord: [];
  editRecord: [id: number];
  setFilter: [filter: string];
}>();

const router = useRouter();
const filterVisible = ref(false);

// Task Status Store
const taskStatusStore = useTaskStatusStore();
const { list: TaskStatusList } = storeToRefs(taskStatusStore);

// Computed hours and money from rows
const hours = computed(() => {
  let hrs = 0;
  for (let i = 0; i < props.rows.length; i++) {
    const row = props.rows[i];
    hrs += (row.ActualTime || row.EstimatedTime || 0);
  }
  return hrs;
});

const money = computed(() => {
  return 0; // Placeholder - calculate if needed
});

// Methods
const refresh = () => {
  emit('refresh');
};

const newRecord = () => {
  emit('newRecord');
};

const editRecord = (id: number) => {
  emit('editRecord', id);
};

const setFilter = (filter: string) => {
  emit('setFilter', filter);
  filterVisible.value = false;
};

const getLocalDate = (val: any) => {
  if (!val) return '';
  let tmp = new Date(val);
  return Helper.dateToInput(tmp, Helper.DateFormat_Date);
};

// Get status name by ID
const getStatusName = (statusId: number) => {
  const status = TaskStatusList.value[statusId];
  return status?.Status || `Status #${statusId}`;
};

// Helper function to get Bootstrap badge class based on status
const getStatusBadgeClass = (statusId: number) => {
  const status = TaskStatusList.value[statusId];
  const statusName = status?.Status?.toLowerCase() || '';
  
  if (statusName.includes('completed') || statusName.includes('done')) {
    return 'bg-success';
  } else if (statusName.includes('pending') || statusName.includes('waiting')) {
    return 'bg-warning';
  } else if (statusName.includes('progress') || statusName.includes('active')) {
    return 'bg-primary';
  } else if (statusName.includes('cancelled') || statusName.includes('rejected')) {
    return 'bg-danger';
  } else if (statusName.includes('scheduled') || statusName.includes('planned')) {
    return 'bg-info';
  } else {
    return 'bg-secondary';
  }
};
</script>
